import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import { formatCurrency } from "../format";
import type { Row, TabDef } from "../types";
import {
  IconBriefcase,
  IconCalendar,
  IconClipboard,
  IconClose,
  IconDashboard,
  IconEdit,
  IconFile,
  IconPlus,
  IconSearch,
  IconShield,
  IconTrash,
  IconTrendUp,
  IconUsers,
} from "../icons";
import ErrorState from "./ErrorState";
import RecordForm from "./RecordForm";
import { usePodeExcluir } from "../permissoes";
import { compararNomes } from "../ordem";
import { NOTAS_PLANO, norm, vigenteDoCargo, type Vigente } from "../plano-cargos";
import { BaseVigentePlano, CriteriosPlano, TrilhaEtapas, TrilhasPlano } from "./PlanoSecoes";
import { MovimentacoesPlano, PainelPlano } from "./PlanoMovimentacoes";

/**
 * ABA "CARGOS E SALÁRIOS" — o Plano de Cargos e Salários 2026.
 *
 * Cada aba da planilha virou uma seção aqui dentro (sem item novo no menu):
 *   Cargos (ficha de cada cargo, que abre ao clicar) · Trilhas de crescimento ·
 *   Critérios e uso · Base vigente · Movimentações · Painel anual.
 *
 * Os cargos aparecem por EIXO (o setor do plano), e dentro do eixo na ordem de
 * carreira da planilha (auxiliar → júnior → pleno → liderança). A tabela
 * anterior (jul/2025, cargo × nível com o marcador R$ 1.000) fica arquivada no
 * rodapé: não aparece no popup de cargo, mas segue no banco para reconhecer
 * cadastros antigos.
 *
 * Quem altera: o administrador ou o responsável pelo plano (marcado pelo
 * administrador no botão "Responsáveis"). Os demais só consultam — os botões
 * de gravar nem aparecem, e o servidor recusa (server/plano-cargos.ts).
 */

interface Props {
  tab: TabDef;
  colaboradores: Row[];
  onDataChanged: () => void;
}

type Secao = "cargos" | "trilhas" | "criterios" | "base" | "movimentacoes" | "painel";

const SECOES: { key: Secao; label: string; Icon: typeof IconBriefcase }[] = [
  { key: "cargos", label: "Cargos", Icon: IconBriefcase },
  { key: "trilhas", label: "Trilhas de crescimento", Icon: IconTrendUp },
  { key: "criterios", label: "Critérios e uso", Icon: IconClipboard },
  { key: "base", label: "Base vigente", Icon: IconUsers },
  { key: "movimentacoes", label: "Movimentações", Icon: IconCalendar },
  { key: "painel", label: "Painel anual", Icon: IconDashboard },
];

export interface Plano {
  cargos: Row[];
  base: Row[];
  trilhas: Row[];
  etapas: Row[];
  criterios: Row[];
}

export interface Responsavel {
  usuario: string;
  nome: string | null;
  cargo: string | null;
  admin: boolean;
  responsavel: boolean;
}

interface Acesso {
  podeEditar: boolean;
  ehAdmin: boolean;
  responsaveis?: Responsavel[];
}

const SEM_EIXO = "Outros cargos";

/** Trilha de um cargo (pela coluna "cargo vigente" da trilha). */
export function trilhaDoCargo(cargo: unknown, plano: Plano): Row | null {
  return plano.trilhas.find((t) => norm(t.cargo_vigente) === norm(cargo)) ?? null;
}

export default function CargosView({ tab, colaboradores, onDataChanged }: Props) {
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)
  const [secao, setSecao] = useState<Secao>("cargos");
  const [plano, setPlano] = useState<Plano>({ cargos: [], base: [], trilhas: [], etapas: [], criterios: [] });
  const [acesso, setAcesso] = useState<Acesso>({ podeEditar: false, ehAdmin: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [cargos, base, trilhas, etapas, criterios, ac] = await Promise.all([
        api.list(tab.resource),
        api.list("cargos_base_vigente"),
        api.list("cargos_trilhas"),
        api.list("cargos_trilha_etapas"),
        api.list("cargos_criterios"),
        api.planoCargos.acesso(),
      ]);
      setPlano({ cargos, base, trilhas, etapas, criterios });
      setAcesso(ac);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource]);

  useEffect(() => {
    load();
  }, [load]);

  const recarregar = useCallback(async () => {
    await load();
    onDataChanged();
  }, [load, onDataChanged]);

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  const podeEditar = acesso.podeEditar;

  return (
    <div className="crud endo plano">
      <nav className="endo-abas">
        {SECOES.map(({ key, label, Icon }) => (
          <button key={key} className={secao === key ? "ativa" : ""} onClick={() => setSecao(key)}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="empty">Carregando o plano...</div>
      ) : (
        <>
          {secao === "cargos" && (
            <CargosPlano
              tab={tab}
              plano={plano}
              acesso={acesso}
              podeExcluir={podeExcluir}
              recarregar={recarregar}
            />
          )}
          {secao === "trilhas" && (
            <TrilhasPlano plano={plano} podeEditar={podeEditar} podeExcluir={podeExcluir} recarregar={recarregar} />
          )}
          {secao === "criterios" && (
            <CriteriosPlano plano={plano} podeEditar={podeEditar} podeExcluir={podeExcluir} recarregar={recarregar} />
          )}
          {secao === "base" && (
            <BaseVigentePlano plano={plano} podeEditar={podeEditar} podeExcluir={podeExcluir} recarregar={recarregar} />
          )}
          {secao === "movimentacoes" && (
            <MovimentacoesPlano
              plano={plano}
              colaboradores={colaboradores}
              podeEditar={podeEditar}
              podeExcluir={podeExcluir}
            />
          )}
          {secao === "painel" && <PainelPlano plano={plano} />}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Seção "Cargos"                                                            */
/* ------------------------------------------------------------------------ */

interface CargosProps {
  tab: TabDef;
  plano: Plano;
  acesso: Acesso;
  podeExcluir: boolean;
  recarregar: () => Promise<void>;
}

function CargosPlano({ tab, plano, acesso, podeExcluir, recarregar }: CargosProps) {
  const [search, setSearch] = useState("");
  const [ficha, setFicha] = useState<Row | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [responsaveisAberto, setResponsaveisAberto] = useState(false);
  const podeEditar = acesso.podeEditar;

  /** A busca vale para cargo, eixo, nível, CBO e departamento. */
  const filtrados = useMemo(() => {
    const q = norm(search);
    if (!q) return plano.cargos;
    return plano.cargos.filter((r) =>
      [r.cargo, r.eixo, r.nivel, r.cbo, r.departamento].some((v) => norm(v).includes(q)),
    );
  }, [plano.cargos, search]);

  /** Eixos em ordem alfabética; dentro do eixo, a ordem de carreira do plano. */
  const secoes = useMemo(() => {
    const mapa = new Map<string, Row[]>();
    for (const r of filtrados) {
      const eixo = String(r.eixo ?? r.departamento ?? "").trim() || SEM_EIXO;
      mapa.set(eixo, [...(mapa.get(eixo) ?? []), r]);
    }
    return [...mapa.entries()]
      .sort(([a], [b]) => (a === SEM_EIXO ? 1 : b === SEM_EIXO ? -1 : compararNomes(a, b)))
      .map(([eixo, cargos]) => ({
        eixo,
        cargos: [...cargos].sort(
          (a, b) => (Number(a.ordem) || 9999) - (Number(b.ordem) || 9999) || compararNomes(a.cargo, b.cargo),
        ),
      }));
  }, [filtrados]);

  function abrirEdicao(row: Row | null) {
    setEditing(row);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row) {
    if (editing) {
      await api.update(tab.resource, editing.id, data);
    } else {
      // Cargo novo entra no plano vigente (e não na tabela arquivada).
      await api.create(tab.resource, { ...data, plano: "2026", arquivado: false });
    }
    setFormOpen(false);
    setFicha(null);
    await recarregar();
  }

  async function handleDelete(row: Row) {
    if (!confirm(`Excluir o cargo "${row.cargo}" do plano?`)) return;
    try {
      await api.remove(tab.resource, row.id);
      setFicha(null);
      await recarregar();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  const ocupantes = plano.base.length;

  return (
    <>
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar cargo, eixo, nível ou CBO..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">
            {filtrados.length} cargo(s) · {ocupantes} na base vigente
          </span>
          {acesso.ehAdmin && (
            <button className="btn-secondary" onClick={() => setResponsaveisAberto(true)}>
              <IconShield size={16} /> Responsáveis
            </button>
          )}
          {podeEditar && (
            <button className="btn-primary" onClick={() => abrirEdicao(null)}>
              <IconPlus size={17} /> Novo cargo
            </button>
          )}
        </div>
      </div>

      <p className="plano-nota">
        {NOTAS_PLANO.plano}
        {!podeEditar && " Somente o administrador ou o responsável pelo plano alteram estas informações."}
      </p>

      {secoes.length === 0 ? (
        <div className="empty">
          {search ? `Nenhum cargo encontrado para "${search}".` : "Nenhum cargo no plano ainda."}
        </div>
      ) : (
        <div className="plano-eixos">
          {secoes.map((s) => (
            <section key={s.eixo} className="cargo-secao">
              <div className="cargo-secao-head">
                <span className="cargo-secao-nome">
                  <IconBriefcase size={15} /> {s.eixo}
                </span>
                <span className="cargo-secao-contagem">
                  {s.cargos.length} cargo{s.cargos.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="plano-cards">
                {s.cargos.map((c) => (
                  <CartaoCargo
                    key={c.id}
                    cargo={c}
                    vigente={vigenteDoCargo(c.cargo, plano.base)}
                    podeEditar={podeEditar}
                    podeExcluir={podeExcluir}
                    onAbrir={() => setFicha(c)}
                    onEditar={() => abrirEdicao(c)}
                    onExcluir={() => handleDelete(c)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <TabelaArquivada podeEditar={podeEditar} recarregar={recarregar} resource={tab.resource} />

      {ficha && (
        <FichaCargo
          cargo={ficha}
          plano={plano}
          podeEditar={podeEditar}
          onEditar={() => abrirEdicao(ficha)}
          onFechar={() => setFicha(null)}
        />
      )}

      {formOpen && (
        <RecordForm
          title={editing ? `Editar cargo — ${editing.cargo}` : "Novo cargo no plano"}
          fields={tab.fields}
          initial={editing}
          colaboradores={[]}
          indiceCargos={null}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {responsaveisAberto && (
        <ResponsaveisModal
          lista={acesso.responsaveis ?? []}
          onFechar={() => setResponsaveisAberto(false)}
          onSalvo={async () => {
            setResponsaveisAberto(false);
            await recarregar();
          }}
        />
      )}
    </>
  );
}

function faixa(v: Vigente): string {
  if (v.qtd === 0) return "Sem ocupante";
  if (v.minimo === v.maximo) return formatCurrency(v.minimo);
  return `${formatCurrency(v.minimo)} a ${formatCurrency(v.maximo)}`;
}

interface CartaoProps {
  cargo: Row;
  vigente: Vigente;
  podeEditar: boolean;
  podeExcluir: boolean;
  onAbrir: () => void;
  onEditar: () => void;
  onExcluir: () => void;
}

function CartaoCargo({ cargo, vigente, podeEditar, podeExcluir, onAbrir, onEditar, onExcluir }: CartaoProps) {
  return (
    <div
      className="plano-cartao"
      role="button"
      tabIndex={0}
      title="Abrir a ficha do cargo"
      onClick={onAbrir}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onAbrir())}
    >
      <div className="plano-cartao-topo">
        <strong>{String(cargo.cargo ?? "")}</strong>
        <span className="cargo-acoes" onClick={(e) => e.stopPropagation()}>
          {podeEditar && (
            <button className="icon-btn" title="Editar a ficha do cargo" onClick={onEditar}>
              <IconEdit size={15} />
            </button>
          )}
          {podeExcluir && (
            <button className="icon-btn icon-btn-danger" title="Excluir o cargo" onClick={onExcluir}>
              <IconTrash size={15} />
            </button>
          )}
        </span>
      </div>
      <div className="plano-cartao-meta">
        {cargo.nivel ? <span className="pill pill-neutral">{String(cargo.nivel)}</span> : null}
        <span>CBO {String(cargo.cbo ?? "—")}</span>
      </div>
      <div className="plano-cartao-valores">
        <span>
          <small>Salário de referência</small>
          <b>{formatCurrency(cargo.salario)}</b>
        </span>
        <span>
          <small>
            Vigente · {vigente.qtd} pessoa{vigente.qtd === 1 ? "" : "s"}
          </small>
          <b>{faixa(vigente)}</b>
        </span>
      </div>
      <div className="plano-cartao-rodape">
        <span className={vigente.situacao === "Há dispersão salarial" ? "plano-situacao alerta" : "plano-situacao"}>
          {vigente.situacao}
        </span>
        {cargo.proximo_passo ? <span className="plano-proximo">Próximo: {String(cargo.proximo_passo)}</span> : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Ficha do cargo (a aba "Cargos por Blocos" da planilha)                    */
/* ------------------------------------------------------------------------ */

const ITENS_FICHA: [string, string][] = [
  ["missao", "Missão do cargo"],
  ["atribuicoes", "Principais atribuições"],
  ["escolaridade", "Escolaridade mínima"],
  ["experiencia", "Experiência recomendada"],
  ["conhecimentos", "Conhecimentos técnicos"],
  ["competencias", "Competências comportamentais"],
  ["indicadores", "Indicadores sugeridos"],
  ["proximo_passo", "Próximo passo de carreira"],
];

interface FichaProps {
  cargo: Row;
  plano: Plano;
  podeEditar: boolean;
  onEditar: () => void;
  onFechar: () => void;
}

function FichaCargo({ cargo, plano, podeEditar, onEditar, onFechar }: FichaProps) {
  const v = vigenteDoCargo(cargo.cargo, plano.base);
  const trilha = trilhaDoCargo(cargo.cargo, plano);
  const etapas = trilha ? plano.etapas.filter((e) => Number(e.trilha_id) === Number(trilha.id)) : [];

  return (
    <div className="modal-overlay" onMouseDown={onFechar}>
      <div className="modal-card modal-ficha plano-ficha" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{String(cargo.cargo ?? "")}</h3>
            <p className="modal-sub">
              {[cargo.eixo, cargo.nivel, cargo.cbo ? `CBO ${cargo.cbo}` : null, cargo.departamento ? `Departamento: ${cargo.departamento}` : null]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onFechar} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="plano-numeros">
            <div>
              <small>Qtd. vigente</small>
              <b>{v.qtd}</b>
            </div>
            <div>
              <small>Salário mínimo vigente</small>
              <b>{formatCurrency(v.minimo)}</b>
            </div>
            <div>
              <small>Salário médio vigente</small>
              <b>{formatCurrency(v.medio)}</b>
            </div>
            <div>
              <small>Salário máximo vigente</small>
              <b>{formatCurrency(v.maximo)}</b>
            </div>
            <div>
              <small>Situação salarial</small>
              <b>{v.situacao}</b>
            </div>
            <div>
              <small>Salário de referência</small>
              <b>{formatCurrency(cargo.salario)}</b>
            </div>
          </div>

          <dl className="plano-ficha-lista">
            {ITENS_FICHA.map(([campo, rotulo]) => (
              <div key={campo}>
                <dt>{rotulo}</dt>
                <dd>{cargo[campo] ? String(cargo[campo]) : "—"}</dd>
              </div>
            ))}
          </dl>

          {v.linhas.length > 0 && (
            <>
              <h4 className="plano-subtitulo">Na base vigente</h4>
              <div className="table-wrap">
                <table className="table plano-tabela">
                  <thead>
                    <tr>
                      <th>CBO informado</th>
                      <th>Contrato</th>
                      <th className="num">Salário-base vigente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.linhas.map((b) => (
                      <tr key={b.id}>
                        <td>{String(b.cbo ?? "—")}</td>
                        <td>{String(b.contrato ?? "—")}</td>
                        <td className="num">{formatCurrency(b.salario_base)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h4 className="plano-subtitulo">
            Plano de crescimento{trilha?.familia ? ` — família ${trilha.familia}` : ""}
          </h4>
          {trilha ? (
            <TrilhaEtapas etapas={etapas} destaque={cargo} observacao={trilha.observacao} />
          ) : (
            <p className="plano-nota">Este cargo ainda não tem trilha. Crie em "Trilhas de crescimento".</p>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn-secondary" onClick={onFechar}>
            Fechar
          </button>
          {podeEditar && (
            <button className="btn-primary" onClick={onEditar}>
              <IconEdit size={16} /> Editar ficha
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Responsáveis pelo plano (só o administrador escolhe)                      */
/* ------------------------------------------------------------------------ */

function ResponsaveisModal({
  lista,
  onFechar,
  onSalvo,
}: {
  lista: Responsavel[];
  onFechar: () => void;
  onSalvo: () => Promise<void>;
}) {
  const [marcados, setMarcados] = useState<Set<string>>(
    () => new Set(lista.filter((r) => r.responsavel).map((r) => r.usuario)),
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ordenados = useMemo(() => [...lista].sort((a, b) => compararNomes(a.nome ?? a.usuario, b.nome ?? b.usuario)), [lista]);

  function alternar(u: string) {
    setMarcados((prev) => {
      const s = new Set(prev);
      if (s.has(u)) s.delete(u);
      else s.add(u);
      return s;
    });
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      await api.planoCargos.salvarResponsaveis([...marcados]);
      await onSalvo();
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao salvar");
      setSalvando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onFechar}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Responsáveis pelo plano</h3>
            <p className="modal-sub">Quem, além dos administradores, pode alterar o Plano de Cargos e Salários.</p>
          </div>
          <button type="button" className="icon-btn" onClick={onFechar} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="plano-responsaveis">
            {ordenados.map((r) => (
              <label key={r.usuario} className={r.admin ? "desabilitado" : ""}>
                <input
                  type="checkbox"
                  checked={r.admin || marcados.has(r.usuario)}
                  disabled={r.admin}
                  onChange={() => alternar(r.usuario)}
                />
                <span>
                  <b>{r.nome || r.usuario}</b>
                  <small>{r.admin ? "Administrador — já pode alterar" : r.cargo || r.usuario}</small>
                </span>
              </label>
            ))}
            {ordenados.length === 0 && <p className="plano-nota">Nenhum usuário ativo com acesso ao RH.</p>}
          </div>
        </div>
        <div className="modal-foot">
          {erro && <div className="form-error">{erro}</div>}
          <button className="btn-secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button className="btn-primary" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Tabela anterior (jul/2025), arquivada                                     */
/* ------------------------------------------------------------------------ */

function TabelaArquivada({
  podeEditar,
  recarregar,
  resource,
}: {
  podeEditar: boolean;
  recarregar: () => Promise<void>;
  resource: string;
}) {
  const [aberta, setAberta] = useState(false);
  const [linhas, setLinhas] = useState<Row[] | null>(null);

  useEffect(() => {
    if (!aberta || linhas) return;
    api
      .listTodos(resource)
      .then((rows) => setLinhas(rows.filter((r) => r.arquivado === true)))
      .catch(() => setLinhas([]));
  }, [aberta, linhas, resource]);

  async function reativar(r: Row) {
    if (!confirm(`Trazer "${r.cargo}${r.nivel ? ` - ${r.nivel}` : ""}" de volta para o plano?`)) return;
    try {
      await api.update(resource, r.id, { arquivado: false, plano: "2026" });
      setLinhas(null);
      await recarregar();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao reativar");
    }
  }

  return (
    <div className="plano-arquivo">
      <button className="btn-link" onClick={() => setAberta((a) => !a)}>
        <IconFile size={15} /> {aberta ? "Esconder" : "Ver"} a tabela anterior (arquivada)
      </button>
      {aberta && (
        <>
          <p className="plano-nota">
            Tabela de cargos de julho/2025 (cargo × nível). Saiu da lista e do popup de cargo com o plano 2026, mas
            continua guardada: é ela que ainda reconhece cadastros antigos, como "Eletricista - Pleno".
          </p>
          {linhas === null ? (
            <div className="empty">Carregando...</div>
          ) : linhas.length === 0 ? (
            <div className="empty">Nada arquivado.</div>
          ) : (
            <div className="plano-arquivados">
              {linhas.map((r) => (
                <span key={r.id} className="plano-arquivado">
                  {String(r.cargo)}
                  {r.nivel ? ` - ${r.nivel}` : ""} <small>{String(r.departamento ?? "")}</small>
                  {podeEditar && (
                    <button className="btn-link" onClick={() => reativar(r)}>
                      reativar
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
